import type { ReleaseConfiguration } from "@insights/contracts";

export function getReleaseConfigurationError(
  configuration: ReleaseConfiguration,
) {
  if (!configuration.deploymentTargets.some((target) => target.trim())) {
    return "Add at least one release target.";
  }

  for (const [index, profile] of configuration.profiles.entries()) {
    if (profile.repository !== undefined && !profile.repository.trim()) {
      return `Profile ${index + 1} needs a repository.`;
    }
    if (!profile.name.trim()) {
      return `Profile ${index + 1} needs a name.`;
    }
    if (!profile.environment.trim()) {
      return `Profile ${index + 1} needs an environment.`;
    }
    if (!profile.branchPattern.trim()) {
      return `Profile ${index + 1} needs a branch pattern.`;
    }
  }
  const duplicateProfile = findDuplicate(
    configuration.profiles.map((profile) => profile.name),
  );
  if (duplicateProfile) {
    return `Profile names must be unique. Duplicate: ${duplicateProfile}.`;
  }

  if (!configuration.releaseNotesCategories.length) {
    return "Add at least one release-note category.";
  }
  for (const [index, category] of configuration.releaseNotesCategories.entries()) {
    if (!category.name.trim()) {
      return `Category ${index + 1} needs a name.`;
    }
    if (!category.emoji.trim()) {
      return `Category ${index + 1} needs an emoji.`;
    }
    if (!category.color.trim()) {
      return `Category ${index + 1} needs a color.`;
    }
  }

  const aliases = new Map<string, string>();
  for (const category of configuration.releaseNotesCategories) {
    for (const alias of [...category.aliases, category.name]) {
      const key = alias.trim().toLocaleLowerCase();
      const owner = aliases.get(key);
      if (owner && owner !== category.name.toLocaleLowerCase()) {
        return `Aliases must be unique across categories. Duplicate: ${alias.trim()}.`;
      }
      aliases.set(key, category.name.toLocaleLowerCase());
    }
  }

  const fallback = configuration.releaseNotesFallbackCategory;
  if (!fallback.name.trim()) {
    return "The fallback category needs a name.";
  }
  if (!fallback.emoji.trim()) {
    return "The fallback category needs an emoji.";
  }
  if (!fallback.color.trim()) {
    return "The fallback category needs a color.";
  }

  return null;
}

function findDuplicate(values: string[]) {
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = value.trim().toLocaleLowerCase();
    if (seen.has(normalized)) {
      return value.trim();
    }
    seen.add(normalized);
  }
  return null;
}
